import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { useAdminBasePath } from '@/lib/adminPath';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, History as HistoryIcon, Search, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { SupportActorMark } from '@/components/support-actor-mark';
import { AUDIT_ACTION_LABELS, auditActionLabel } from '@/lib/auditActions';

type AuditLog = {
  id: number;
  entityType: string;
  entityId: number;
  action: string;
  oldValue: string | null;
  newValue: string | null;
  metadata: {
    accountId?: number;
    accountName?: string;
    orderId?: number;
    productId?: number;
    productName?: string;
    userId?: number;
    username?: string;
    actor?: 'super_admin';
  } | null;
  createdAt: string;
};

export default function History() {
  const adminBase = useAdminBasePath();
  const [productFilter, setProductFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  const { data: logs, isLoading } = useQuery<AuditLog[]>({
    queryKey: ['/api/audit-logs'],
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });

  // Filter logs based on search criteria
  const filteredLogs = useMemo(() => {
    if (!logs) return [];
    
    return logs.filter(log => {
      // Exclude FreshOrder's own entries (impersonation, add-on switches)
      if (log.action.startsWith('super_admin_')) {
        return false;
      }

      // Product name filter
      if (productFilter && !log.metadata?.productName?.toLowerCase().includes(productFilter.toLowerCase())) {
        return false;
      }

      // Account name filter
      if (accountFilter && !log.metadata?.accountName?.toLowerCase().includes(accountFilter.toLowerCase())) {
        return false;
      }
      
      // Action type filter
      if (actionFilter !== 'all' && log.action !== actionFilter) {
        return false;
      }
      
      return true;
    });
  }, [logs, productFilter, accountFilter, actionFilter]);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'quantity_changed':
        return <Badge className="bg-blue-500">Quantity Changed</Badge>;
      case 'procurement_reset':
        return <Badge className="bg-orange-500">Procurement Reset</Badge>;
      default:
        return <Badge className="bg-gray-500">{auditActionLabel(action)}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EBE1] dark:bg-gray-900">
      <div className="container mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href={`${adminBase}/settings`}>
              <Button variant="ghost" size="icon" data-testid="button-back">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <HistoryIcon className="w-8 h-8" />
                Change History
              </h1>
              <p className="text-muted-foreground">
                Track all order changes and modifications
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Product</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search product..."
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    className="pl-9"
                    data-testid="input-product-filter"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Account</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search account..."
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                    className="pl-9"
                    data-testid="input-account-filter"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Action Type</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger data-testid="select-action-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(productFilter || accountFilter || actionFilter !== 'all') && (
              <div className="mt-4 flex items-center gap-2">
                <Badge variant="secondary">
                  {filteredLogs.length} result{filteredLogs.length !== 1 ? 's' : ''}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setProductFilter('');
                    setAccountFilter('');
                    setActionFilter('all');
                  }}
                  data-testid="button-clear-filters"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Changes</CardTitle>
            <CardDescription>
              Complete audit trail of order item modifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                Loading history...
              </div>
            ) : !filteredLogs || filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {logs && logs.length > 0 ? 'No results match your filters' : 'No changes recorded yet'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Old Value</TableHead>
                      <TableHead>New Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                        <TableCell className="font-medium">
                          {format(new Date(log.createdAt), 'MMM dd, yyyy h:mm a')}
                        </TableCell>
                        <TableCell>
                          {getActionBadge(log.action)}
                          <SupportActorMark actor={log.metadata?.actor} />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{log.metadata?.productName ?? 'N/A'}</div>
                          <div className="text-sm text-muted-foreground">
                            Order #{log.metadata?.orderId ?? '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.metadata?.accountName ?? 'N/A'}
                        </TableCell>
                        <TableCell className="text-red-600 font-medium max-w-xs">
                          <div className="line-clamp-3 whitespace-pre-wrap break-words" title={log.oldValue ?? undefined}>
                            {log.oldValue || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="text-green-600 font-medium max-w-xs">
                          <div className="line-clamp-3 whitespace-pre-wrap break-words" title={log.newValue ?? undefined}>
                            {log.newValue || '—'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
